// OwnerIntakeFormPage — /owner-intake/:token. PUBLIC, owner-facing.
//
// The owner-INTAKE front door to the Exclusive Property Management Agreement
// (EPMA). Each owner is emailed a per-owner tokenized link here; they answer only
// the questions that APPLY (the SERVER decides which, from the staff prefill + the
// owner's prior answers), and the first owner to Submit locks the intake and the
// agreement auto-generates + goes out for signature.
//
// Like IntakeFormPage / MoveoutFundsIntakeFormPage this is a standalone branded
// surface (NOT wrapped in the authenticated AppShell — see App.tsx) and talks
// ONLY to the public, token-gated /api/owner-intake/:token endpoints via
// lib/ownerIntakeClient.ts (no Bearer token).
//
// Three behaviors carry the page (all server-driven):
//   1. ADAPTIVE QUESTIONS. The server returns `questions` in on-screen order,
//      grouped by section. We render each by kind. On any change we save (POST
//      without complete) and RE-FETCH the view, so answering a gate (hasIrrigation,
//      needsWinterizing, inHoa) reveals/hides the dependent questions the server
//      now computes. Some questions are CONFIRM questions (staff proposed an
//      answer, pre-selected) — leaving one as-is confirms it.
//   2. HOA STEP (low-friction — NO inline upload). When the property resolves in-HOA
//      the server adds a WHICH-HOA `select` question (known HOAs + "New / not listed")
//      and — when "New / not listed" is picked — a freeform HOA-name text question.
//      These are ordinary server-driven questions (rendered by QuestionField), so the
//      page needs no special HOA UI. The intake captures only the CHOICE; the CC&Rs
//      are collected later via a SEPARATE emailed /ccr-upload link (for a new HOA).
//   3. GENERATING → SIGN. On Submit (POST complete): a 400 highlights the missing
//      fields; a winning completion returns signUrl → redirect; a losing/late
//      completion returns state 'generating' → we show "we're writing it up…" and
//      POLL the GET view until state 'ready', then redirect to signUrl.
//
// Per Miles: the intake is a FORM (formy/straightforward is fine) — the AGREEMENT
// is the bespoke artifact, not the intake. Kept clean and clear, not over-designed.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getOwnerIntake,
  submitOwnerIntake,
  OwnerIntakeApiError,
  type OwnerIntakeAnswers,
  type OwnerIntakeFormView,
  type OwnerIntakeQuestion,
  type OwnerIntakeSection,
} from '@/lib/ownerIntakeClient';

// ---------------------------------------------------------------------------
// Brand header (shared lockup with the other public form pages, DS tokens)
// ---------------------------------------------------------------------------

function BrandLockup({ propertyAddress }: { propertyAddress: string | null }): React.JSX.Element {
  return (
    <header style={{ textAlign: 'center', marginBottom: 'var(--space-7)' }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          lineHeight: 1.2,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--fg)',
        }}
      >
        Welcome&nbsp;Home
      </div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 13,
          lineHeight: 1.2,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          paddingLeft: '0.42em',
        }}
      >
        Properties
      </div>
      <div
        style={{
          width: 56,
          height: 1,
          backgroundColor: 'var(--accent)',
          margin: 'var(--space-4) auto 0',
        }}
      />
      {propertyAddress ? (
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--fg-muted)',
          }}
        >
          {propertyAddress}
        </p>
      ) : null}
    </header>
  );
}

function SectionCard(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h2
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--text-lg)',
            fontWeight: 400,
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          {props.title}
        </h2>
        {props.description ? (
          <p className="modal-description" style={{ marginTop: 'var(--space-2)' }}>
            {props.description}
          </p>
        ) : null}
      </div>
      {props.children}
    </section>
  );
}

function radioTileStyle(selected: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-3) var(--space-4)',
    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    backgroundColor: selected ? 'var(--accent-soft)' : 'var(--surface)',
  };
}

// ---------------------------------------------------------------------------
// Post-submit confirmation / processing screens (FIX 4)
// ---------------------------------------------------------------------------
//
// After a successful submit we REPLACE the form with a clear, owner-voiced screen —
// never just a disabled button. Two shapes:
//   • preparing — the agreement is being written up ("We're preparing your
//                 agreement.") with a subtle processing indicator.
//   • ready     — it's out for signature; a "Review & sign →" action.
// Per the Bespoke Communications Doctrine: this is the OWNER's voice about THEIR
// agreement for THEIR property, right now — no leftover template language.

/** The owner's first name for the salutation, or '' when unknown. */
function ownerFirstName(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '';
  return n.split(/\s+/)[0] ?? '';
}

/** A subtle three-dot "working…" indicator (CSS keyframes, scoped by class). */
function ProcessingDots(): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{ display: 'inline-flex', gap: 6, marginTop: 'var(--space-5)' }}
    >
      <style>{`
        @keyframes oi-dot { 0%, 80%, 100% { opacity: 0.25; } 40% { opacity: 1; } }
        .oi-dot { width: 8px; height: 8px; border-radius: 50%;
          background: var(--accent); display: inline-block;
          animation: oi-dot 1.2s ease-in-out infinite; }
        .oi-dot:nth-child(2) { animation-delay: 0.2s; }
        .oi-dot:nth-child(3) { animation-delay: 0.4s; }
        @media (prefers-reduced-motion: reduce) { .oi-dot { animation: none; opacity: 0.6; } }
      `}</style>
      <span className="oi-dot" />
      <span className="oi-dot" />
      <span className="oi-dot" />
    </span>
  );
}

const confirmHeadlineStyle: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontSize: 26,
  fontWeight: 400,
  margin: 0,
  color: 'var(--fg)',
};

const confirmEyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-xs)',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--accent)',
  marginBottom: 'var(--space-3)',
};

/**
 * The bespoke confirmation card the owner sees after submitting. `preparing` shows the
 * "we're writing it up / we'll email you" state with the processing dots; `ready` shows
 * the "Review & sign →" action. `firstName`/`address` personalize the copy.
 */
function SubmittedCard(props: {
  kind: 'preparing' | 'ready';
  firstName: string;
  address: string;
  signUrl?: string;
}): React.JSX.Element {
  const { kind, firstName, address } = props;
  const thanks = firstName ? `Thanks, ${firstName} — ` : 'Thanks — ';
  const forAddress = address ? ` for ${address}` : '';
  if (kind === 'ready') {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <div style={confirmEyebrowStyle}>All set</div>
        <h1 style={confirmHeadlineStyle}>Your agreement is ready.</h1>
        <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
          {thanks}we&apos;ve prepared your management agreement{forAddress}. Give it a look and add
          your signature — it only takes a moment.
        </p>
        {props.signUrl ? (
          <div style={{ marginTop: 'var(--space-5)' }}>
            <a
              className="btn"
              data-size="lg"
              href={props.signUrl}
              style={{ textDecoration: 'none' }}
            >
              Review &amp; sign &rarr;
            </a>
          </div>
        ) : (
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            Check your email for the link to sign.
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="card" style={{ textAlign: 'center' }} aria-busy="true">
      <div style={confirmEyebrowStyle}>All set</div>
      <h1 style={confirmHeadlineStyle}>We&apos;re preparing your agreement.</h1>
      <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
        {thanks}we have everything we need and we&apos;re writing up your management agreement
        {forAddress} now. We&apos;ll email it to you to sign in just a moment — you can leave this
        page open.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <ProcessingDots />
      </div>
    </div>
  );
}

// Human titles / blurbs per section (grouping headers). The prompts themselves
// come from the server; these frame the group.
const SECTION_META: Record<OwnerIntakeSection, { title: string; description?: string }> = {
  taxes_insurance: {
    title: 'Taxes, insurance & mortgage',
    description:
      'Whether WHP pays the property taxes, insurance, and mortgage on your behalf.',
  },
  irrigation: { title: 'Irrigation & winterization' },
  hoa: { title: 'Homeowners association (HOA)' },
  notice: {
    title: 'Address for legal notices',
    description: 'Where formal notices about this agreement should be mailed. Required.',
  },
};

const SECTION_ORDER: OwnerIntakeSection[] = [
  'taxes_insurance',
  'irrigation',
  'hoa',
  'notice',
];

// Polling for the generating→ready transition.
const POLL_INTERVAL_MS = 2500;
const POLL_MAX_MS = 120000;

/**
 * Seed the form defaults into the answer set so an un-edited default still SUBMITS:
 *
 *   • CONFIRM-WITH-OWNER: a confirm question is pre-selected to the staff-proposed
 *     value, so leaving it as-is means "confirmed." We fold that value in for any
 *     confirm question the owner hasn't answered.
 *   • NOTICE EMAIL (§25): pre-populate with the owner's own email (view
 *     .noticeEmailDefault) when they haven't entered one — so it's not blank just
 *     because it was prefilled. The owner can still edit it in the field.
 *
 * Folding both into the answers means required-completeness passes and the value is
 * actually POSTed on submit — not merely shown.
 */
function withDefaults(
  view: OwnerIntakeFormView,
  answers: OwnerIntakeAnswers,
): OwnerIntakeAnswers {
  let next = answers;
  for (const q of view.questions) {
    if (q.confirm && q.kind === 'boolean' && typeof next[q.id] !== 'boolean') {
      next = { ...next, [q.id]: q.confirm.staffValue };
    }
  }
  // Pre-fill the §25 notice email with the owner's own address when unset/blank AND
  // the notice-email question is actually being asked for this intake.
  const asksNoticeEmail = view.questions.some((q) => q.id === 'noticeEmail');
  const currentEmail = typeof next.noticeEmail === 'string' ? next.noticeEmail.trim() : '';
  if (asksNoticeEmail && !currentEmail && view.noticeEmailDefault) {
    next = { ...next, noticeEmail: view.noticeEmailDefault };
  }
  return next;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OwnerIntakeFormPage(): React.JSX.Element {
  const { token = '' } = useParams<{ token: string }>();

  const [view, setView] = useState<OwnerIntakeFormView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);

  // Post-submit outcome (FIX 4): after a successful submit we REPLACE the form with a
  // bespoke confirmation screen — never just a disabled button.
  //   • 'ready'     — the agreement generated + is out for signature (this owner can
  //                   review & sign NOW): show a "Review & sign →" action.
  //   • 'preparing' — the agreement is being written up (a completion won the lock,
  //                   ours or another owner's): show the "we're preparing it / we'll
  //                   email you" processing state. When polling finds the sign URL we
  //                   promote this to 'ready' so the owner can sign without a dead-end.
  const [submitOutcome, setSubmitOutcome] = useState<
    { kind: 'ready'; signUrl: string } | { kind: 'preparing' } | null
  >(null);

  // The working answer set. Seeded from the loaded view, then updated locally as
  // the owner edits; each change is POSTed (save) and the view re-fetched so the
  // question set adapts. Kept as a ref-synced state so save handlers see fresh.
  const [answers, setAnswers] = useState<OwnerIntakeAnswers>({});

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  // --- Initial load. ---
  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError(null);
    getOwnerIntake(token)
      .then((v) => {
        if (!live) return;
        setView(v);
        setAnswers(withDefaults(v, v.answers ?? {}));
        // A returning owner whose agreement is already out → show the bespoke
        // "review & sign" screen (in place, no dead-end) rather than a bare redirect.
        if (v.state === 'ready' && v.signUrl) {
          setSubmitOutcome({ kind: 'ready', signUrl: v.signUrl });
        }
      })
      .catch(() => {
        if (live) setLoadError('We could not load this form. The link may have expired.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  // --- Save partial progress, then RE-FETCH so the question set adapts. ---
  // Called on every field change. The re-fetch is what makes the form adaptive:
  // answering a gate (hasIrrigation / inHoa / hasWinterization…)
  // changes which questions the server computes, so we replace the view wholesale.
  const saveAndRefresh = useCallback(
    async (nextAnswers: OwnerIntakeAnswers) => {
      setSaving(true);
      setSaveError(null);
      try {
        const res = await submitOwnerIntake(token, { answers: nextAnswers });
        if (!alive.current) return;
        if (res.view) {
          setView(res.view);
          // Keep the server's echoed answers authoritative (drops inapplicable ones),
          // then re-seed confirm defaults for any newly-revealed confirm question.
          setAnswers(withDefaults(res.view, res.view.answers ?? nextAnswers));
        }
      } catch (err) {
        if (!alive.current) return;
        // Injection guard (400) or transient — surface but keep the local edit.
        setSaveError(
          err instanceof OwnerIntakeApiError
            ? (err.errorMessage ?? 'We could not save that. Please try again.')
            : 'We could not save that. Please try again.',
        );
      } finally {
        if (alive.current) setSaving(false);
      }
    },
    [token],
  );

  // Update one answer key + persist. `refetch` true for gate questions (booleans)
  // that change the applicable set; free-text saves on blur (no refetch).
  const setAnswer = useCallback(
    (id: keyof OwnerIntakeAnswers, value: unknown, refetch: boolean) => {
      setMissing((m) => m.filter((k) => k !== id));
      setAnswers((prev) => {
        const next = { ...prev, [id]: value } as OwnerIntakeAnswers;
        if (refetch) void saveAndRefresh(next);
        return next;
      });
    },
    [saveAndRefresh],
  );

  // Free-text: persist on blur (no view refetch — text doesn't gate anything).
  const saveText = useCallback(
    (id: keyof OwnerIntakeAnswers) => {
      setAnswers((prev) => {
        void saveAndRefresh(prev);
        return prev;
      });
      void id;
    },
    [saveAndRefresh],
  );

  // The applicable questions (server-driven). The WHICH-HOA select + freeform-name
  // text are ordinary questions here — no special HOA UI (the CC&Rs are collected
  // later via a separate emailed link, not inline).
  const questions = view?.questions ?? [];

  // --- Poll GET until the agreement is ready, then surface the "Review & sign"
  // action (no dead-end, no hard redirect): the processing screen becomes the
  // ready-to-sign screen in place. ---
  const startPolling = useCallback(() => {
    const startedAt = Date.now();
    const tick = () => {
      if (!alive.current) return;
      getOwnerIntake(token)
        .then((v) => {
          if (!alive.current) return;
          setView(v);
          if (v.state === 'ready' && v.signUrl) {
            setSubmitOutcome({ kind: 'ready', signUrl: v.signUrl });
            return;
          }
          if (Date.now() - startedAt < POLL_MAX_MS) {
            pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (!alive.current) return;
          if (Date.now() - startedAt < POLL_MAX_MS) {
            pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
          }
        });
    };
    pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, [token]);

  // --- Submit (complete). ---
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSaveError(null);
    setMissing([]);
    try {
      const res = await submitOwnerIntake(token, { answers, complete: true });
      if (!alive.current) return;
      // Winning completion (generate+send was synchronous) → the agreement is out and
      // this owner can sign now. Show the bespoke "all set / review & sign" screen —
      // NOT a hard redirect (FIX 4: no dead-ends, clear confirmation).
      if (res.signUrl) {
        setSubmitOutcome({ kind: 'ready', signUrl: res.signUrl });
        return;
      }
      if (res.view) {
        setView(res.view);
        // The record is already ready (e.g. a peer completion finished) → sign action.
        if (res.view.state === 'ready' && res.view.signUrl) {
          setSubmitOutcome({ kind: 'ready', signUrl: res.view.signUrl });
          return;
        }
        // Being written up (ours or a peer's completion won the lock) → the bespoke
        // "we're preparing it / we'll email you" processing screen; poll to promote
        // it to the sign action the moment it's ready.
        if (res.view.state === 'generating') {
          setSubmitOutcome({ kind: 'preparing' });
          startPolling();
          return;
        }
      }
    } catch (err) {
      if (!alive.current) return;
      if (err instanceof OwnerIntakeApiError && err.status === 400) {
        // Missing required (has .missing) OR injection (has .error only).
        if (err.missing.length > 0) {
          setMissing(err.missing);
          setSaveError('Please answer the highlighted questions before submitting.');
        } else {
          setSaveError(err.errorMessage ?? 'Please review your answers and try again.');
        }
      } else {
        setSaveError(
          err instanceof OwnerIntakeApiError
            ? (err.errorMessage ?? 'We could not submit. Please try again.')
            : 'We could not submit. Please try again.',
        );
      }
    } finally {
      if (alive.current) setSubmitting(false);
    }
  }, [token, answers, startPolling]);

  const shell = useCallback(
    (children: React.ReactNode): React.JSX.Element => (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--bg-alt)',
          padding: 'var(--space-7) var(--space-4)',
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>{children}</div>
      </div>
    ),
    [],
  );

  // --- Loading / error / terminal states ---
  if (loading) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <p style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>Loading…</p>
      </>,
    );
  }

  if (loadError || !view) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">This link is no longer available</div>
            {loadError ?? 'Please reply to the email we sent you and a real person will help.'}
          </div>
        </div>
      </>,
    );
  }

  if (view.state === 'unavailable') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <SectionCard
          title="Nothing to fill out right now"
          description="This link is valid, but there's nothing for you to provide at the moment. If we need something from you, we'll send a fresh link."
        >
          <></>
        </SectionCard>
      </>,
    );
  }

  const firstName = ownerFirstName(view.owner?.name);
  const address = view.propertyAddress || '';

  // FIX 4: after a successful submit we own the screen — the bespoke confirmation /
  // processing card, NOT the form (and never just a disabled button). Takes priority
  // over the raw view.state below.
  if (submitOutcome) {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <SubmittedCard
          kind={submitOutcome.kind}
          firstName={firstName}
          address={address}
          {...(submitOutcome.kind === 'ready' ? { signUrl: submitOutcome.signUrl } : {})}
        />
      </>,
    );
  }

  // generating → a completion (this owner's, or another owner's) won the lock and the
  // agreement is being written up. A NON-first completer who lands here directly sees
  // the same bespoke "we're preparing it" state (no dead-end, no raw redirect).
  if (view.state === 'generating') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <SubmittedCard kind="preparing" firstName={firstName} address={address} />
      </>,
    );
  }

  // ready → the agreement is out for signature; this owner can sign now. Show the
  // "Review & sign →" action (a returning owner also lands here).
  if (view.state === 'ready') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress || null} />
        <SubmittedCard
          kind="ready"
          firstName={firstName}
          address={address}
          {...(view.signUrl ? { signUrl: view.signUrl } : {})}
        />
      </>,
    );
  }

  // --- Fillable form ---
  const greetingName = view.owner?.name ? `, ${view.owner.name.split(/\s+/)[0]}` : '';

  // Group the applicable questions by section, preserving section order.
  const bySection: Record<OwnerIntakeSection, OwnerIntakeQuestion[]> = {
    taxes_insurance: [],
    irrigation: [],
    hoa: [],
    notice: [],
  };
  for (const q of questions) bySection[q.section].push(q);

  return shell(
    <>
      <BrandLockup propertyAddress={view.propertyAddress || null} />

      <div style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 26,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg)',
          }}
        >
          Property management agreement{greetingName}
        </h1>
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
          Please answer a few quick questions about {view.propertyAddress || 'your property'} so we
          can finish preparing your agreement for signature.
        </p>
      </div>

      {saveError ? (
        <div className="alert" data-tone="danger" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{saveError}</div>
        </div>
      ) : null}

      {SECTION_ORDER.map((section) => {
        const qs = bySection[section];
        if (qs.length === 0) return null;
        const meta = SECTION_META[section];
        return (
          <SectionCard
            key={section}
            title={meta.title}
            {...(meta.description ? { description: meta.description } : {})}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
              {qs.map((q) => (
                <QuestionField
                  key={q.id}
                  question={q}
                  answers={answers}
                  invalid={missing.includes(String(q.id))}
                  onBoolean={(v) => setAnswer(q.id, v, true)}
                  onSelect={(v) => setAnswer(q.id, v, true)}
                  onText={(v) => setAnswer(q.id, v, false)}
                  onTextBlur={() => saveText(q.id)}
                />
              ))}
            </div>
          </SectionCard>
        );
      })}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-6)',
        }}
      >
        <span className="helper" style={{ color: 'var(--fg-muted)' }}>
          {saving ? 'Saving…' : 'Your answers save automatically.'}
        </span>
        <button
          className="btn"
          data-size="lg"
          disabled={submitting}
          onClick={() => void handleSubmit()}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </>,
  );
}

// ---------------------------------------------------------------------------
// One question, rendered by kind
// ---------------------------------------------------------------------------

function QuestionField(props: {
  question: OwnerIntakeQuestion;
  answers: OwnerIntakeAnswers;
  invalid: boolean;
  onBoolean: (v: boolean) => void;
  onSelect: (v: string) => void;
  onText: (v: string) => void;
  onTextBlur: () => void;
}): React.JSX.Element {
  const { question: q, answers, invalid } = props;
  const value = answers[q.id];
  const fieldId = `oi-${String(q.id)}`;

  if (q.kind === 'boolean') {
    const answered = typeof value === 'boolean' ? value : undefined;
    // CONFIRM-WITH-OWNER: staff proposed an answer and asked the owner to confirm.
    // Pre-select the staff value until the owner picks (answered ?? staffValue).
    const v = answered ?? q.confirm?.staffValue;
    const confirming = q.confirm !== undefined;
    return (
      <div className="field" style={{ margin: 0 }}>
        <label style={{ marginBottom: confirming ? 'var(--space-1)' : 'var(--space-2)' }}>
          {q.prompt}
          {q.required ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
        </label>
        {confirming ? (
          <p className="helper" style={{ marginTop: 0, marginBottom: 'var(--space-2)' }}>
            We have this as <strong>{q.confirm!.staffValue ? 'Yes' : 'No'}</strong> — please confirm
            it&apos;s right, or correct it.
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {[
            { label: 'Yes', val: true },
            { label: 'No', val: false },
          ].map((opt) => (
            <label key={opt.label} style={{ ...radioTileStyle(v === opt.val), flex: 1 }}>
              <input
                type="radio"
                name={fieldId}
                checked={v === opt.val}
                onChange={() => props.onBoolean(opt.val)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {invalid ? (
          <span className="helper" data-state="error">
            Please choose an answer.
          </span>
        ) : null}
      </div>
    );
  }

  if (q.kind === 'select') {
    const options = q.options ?? [];
    const v = typeof value === 'string' ? value : '';
    return (
      <div className="field" style={{ margin: 0 }}>
        <label htmlFor={fieldId}>
          {q.prompt}
          {q.required ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
        </label>
        <select
          id={fieldId}
          className="input"
          value={v}
          onChange={(e) => props.onSelect(e.target.value)}
          aria-invalid={invalid}
        >
          <option value="" disabled>
            Choose one…
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {invalid ? (
          <span className="helper" data-state="error">
            Please choose an option.
          </span>
        ) : null}
      </div>
    );
  }

  const strVal = typeof value === 'string' ? value : '';
  if (q.kind === 'textarea') {
    return (
      <div className="field" style={{ margin: 0 }}>
        <label htmlFor={fieldId}>
          {q.prompt}
          {q.required ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
        </label>
        <textarea
          id={fieldId}
          className="input"
          rows={3}
          value={strVal}
          onChange={(e) => props.onText(e.target.value)}
          onBlur={props.onTextBlur}
          aria-invalid={invalid}
        />
        {invalid ? (
          <span className="helper" data-state="error">
            This is required.
          </span>
        ) : null}
      </div>
    );
  }

  // text
  return (
    <div className="field" style={{ margin: 0 }}>
      <label htmlFor={fieldId}>
        {q.prompt}
        {q.required ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
      </label>
      <input
        id={fieldId}
        className="input"
        type="text"
        value={strVal}
        onChange={(e) => props.onText(e.target.value)}
        onBlur={props.onTextBlur}
        aria-invalid={invalid}
      />
      {invalid ? (
        <span className="helper" data-state="error">
          This is required.
        </span>
      ) : null}
    </div>
  );
}
